import { IconButton, usePromptModal } from '@affine/component';
import { CollectionService } from '@affine/core/modules/collection';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import { useCollectionsFeatureEnabled } from '@affine/core/modules/streamlain';
import { WorkbenchService } from '@affine/core/modules/workbench';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { AddCollectionIcon } from '@blocksuite/icons/rc';
import { useLiveData, useServices } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import { CollapsibleSection } from '../../layouts/collapsible-section';
import { NavigationPanelCollectionNode } from '../../nodes/collection';
import { NavigationPanelTreeRoot } from '../../tree';
import { useNavigationTreeEntries } from '../../tree/navigation-model';
import { RootEmpty } from './empty';
import * as styles from './index.css';

export const NavigationPanelCollections = ({
  revealOnExpand = false,
}: {
  revealOnExpand?: boolean;
} = {}) => {
  const t = useI18n();
  const collectionsEnabled = useCollectionsFeatureEnabled();
  const { collectionService, workbenchService, navigationPanelService } =
    useServices({
      CollectionService,
      WorkbenchService,
      NavigationPanelService,
    });
  const collections = useLiveData(collectionService.collections$);
  const { openPromptModal } = usePromptModal();
  const path = useMemo(() => ['collections'], []);
  const navigationRoots = useMemo(
    () =>
      [...collections.values()].map(collection => ({
        kind: 'collection' as const,
        id: collection.id,
        label: collection.name$.value,
      })),
    [collections]
  );
  const navigationEntries = useNavigationTreeEntries(navigationRoots, path);
  const handleCreateCollection = useCallback(() => {
    if (!collectionsEnabled) {
      return;
    }
    openPromptModal({
      title: t['com.streamlain.editCollection.saveCollection'](),
      label: t['com.streamlain.editCollectionName.name'](),
      inputOptions: {
        placeholder: t['com.streamlain.editCollectionName.name.placeholder'](),
      },
      children: (
        <div className={styles.createTips}>
          {t['com.streamlain.editCollectionName.createTips']()}
        </div>
      ),
      confirmText: t['com.streamlain.editCollection.save'](),
      cancelText: t['com.streamlain.editCollection.button.cancel'](),
      confirmButtonOptions: {
        variant: 'primary',
      },
      onConfirm(name) {
        const id = collectionService.createCollection({ name });
        track.$.navigationPanel.organize.createOrganizeItem({
          type: 'collection',
        });
        workbenchService.workbench.openCollection(id);
        navigationPanelService.setCollapsed(path, false);
      },
    });
  }, [
    collectionService,
    collectionsEnabled,
    navigationPanelService,
    openPromptModal,
    path,
    t,
    workbenchService.workbench,
  ]);

  if (!collectionsEnabled) {
    return null;
  }

  return (
    <CollapsibleSection
      navigationEntries={navigationEntries}
      path={path}
      testId="navigation-panel-collections"
      title={t['com.streamlain.rootAppSidebar.collections']()}
      revealOnExpand={revealOnExpand}
      actions={
        <IconButton
          data-testid="navigation-panel-bar-add-collection-button"
          onClick={handleCreateCollection}
          size="16"
          tooltip={t[
            'com.streamlain.rootAppSidebar.explorer.collection-section-add-tooltip'
          ]()}
        >
          <AddCollectionIcon />
        </IconButton>
      }
    >
      <NavigationPanelTreeRoot
        placeholder={<RootEmpty onClickCreate={handleCreateCollection} />}
      >
        {Array.from(collections.values()).map(collection => (
          <NavigationPanelCollectionNode
            key={collection.id}
            collectionId={collection.id}
            reorderable={false}
            location={{
              at: 'navigation-panel:collection:list',
            }}
            parentPath={path}
          />
        ))}
      </NavigationPanelTreeRoot>
    </CollapsibleSection>
  );
};
